import { scriptPath } from "./main";
import { appendScripts, appendStyles } from "./ultil";
import { DateTime } from "luxon";

const TIMEZONE_OFFSET_HOURS = Math.abs(new Date().getTimezoneOffset() / 60);

const chartColors = ["#CB9923", "#6EE4FF"];

const lang = document.documentElement.lang;
const message = {
	"vi-VN": {
		trillion: "nghìn tỷ",
		billion: "tỷ",
		million: "triệu",
	},
	"en-US": {
		trillion: "trillion",
		billion: "billion",
		million: "million",
	},
};

const valueRatio = 1; // This value base on customer require, 1 for 1VND, 1000000 for 1M

export function formatNumber(number, unit = "", roundedNumber = false) {
	// Set default options
	const options = {
		style: "decimal",
	};

	if (roundedNumber) {
		// If rounding is requested, remove all decimal places
		number = Math.round(number);
		options.maximumFractionDigits = 0;
	} else if (unit === "%") {
		// For percentages, force exactly one decimal place
		options.minimumFractionDigits = 1;
		options.maximumFractionDigits = 1;
	}

	// Format the number using the Vietnamese locale for the comma separator
	const formattedNumber = number.toLocaleString("vi-VN", options);

	// Append the unit if it exists
	return unit ? `${formattedNumber} ${unit}` : formattedNumber;
}

export function getTopValue(chart, length = 3) {
	const { data } = chart;
	const { datasets } = data;
	const { data: dataSet } = datasets[0];

	let sortedData = [];
	dataSet.sort((a, b) => b - a);
	const chartType = chart.config.type;
	if (chartType === "bubble") {
		sortedData = dataSet.sort((a, b) => b.r - a.r).slice(0, length);
	} else {
		sortedData = dataSet.slice(0, length);
	}
	return sortedData;
}

export function formatDate(date) {
	const dateObject = DateTime.fromISO(date);
	return dateObject.toFormat("dd/MM/yyyy");
}
export function formatMonth(date) {
	const dateObject = new Date(date);
	const dateISO = dateObject.toISOString();
	return DateTime.fromISO(dateISO).toFormat("MM/yyyy");
}

function checkCollision(rect1, rect2, padding = 5) {
	return !(
		rect1.right + padding < rect2.left ||
		rect1.left - padding > rect2.right ||
		rect1.bottom + padding < rect2.top ||
		rect1.top - padding > rect2.bottom
	);
}

const fontSize = (size) => {
	if (window.innerWidth < 768) {
		return size - 2;
	}
	return size;
};

function findBestLabelPosition(
	x,
	y,
	r,
	labelWidth,
	labelHeight,
	canvasWidth,
	canvasHeight,
	existingLabels,
	allDataPoints
) {
	const rightEdgeThreshold = canvasWidth * 0.8;
	const isCloseToRightEdge = x + r + 12 + labelWidth > rightEdgeThreshold;

	let positions;

	if (isCloseToRightEdge) {
		positions = [
			// Left side positions (prioritized)
			{
				x: x - r - 12 - labelWidth,
				y: y - labelHeight / 2,
				side: "left",
			},
			{
				x: x - r - 12 - labelWidth,
				y: y - labelHeight,
				side: "left-top",
			},
			{ x: x - r - 12 - labelWidth, y: y, side: "left-bottom" },

			// Top positions
			{ x: x - labelWidth / 2, y: y - r - 12 - labelHeight, side: "top" },
			{
				x: x - labelWidth,
				y: y - r - 12 - labelHeight,
				side: "top-left",
			},
			{ x: x, y: y - r - 12 - labelHeight, side: "top-right" },

			// Bottom positions
			{ x: x - labelWidth / 2, y: y + r + 12, side: "bottom" },
			{ x: x - labelWidth, y: y + r + 12, side: "bottom-left" },
			{ x: x, y: y + r + 12, side: "bottom-right" },

			// Right side positions (last resort)
			{ x: x + r + 12, y: y - labelHeight / 2, side: "right" },
			{ x: x + r + 12, y: y - labelHeight, side: "right-top" },
			{ x: x + r + 12, y: y, side: "right-bottom" },
		];
	} else {
		// Default order - prioritize right-side positions
		positions = [
			// Right side positions (prioritized)
			{ x: x + r + 12, y: y - labelHeight / 2, side: "right" },
			{ x: x + r + 12, y: y - labelHeight, side: "right-top" },
			{ x: x + r + 12, y: y, side: "right-bottom" },

			// Left side positions
			{
				x: x - r - 12 - labelWidth,
				y: y - labelHeight / 2,
				side: "left",
			},
			{
				x: x - r - 12 - labelWidth,
				y: y - labelHeight,
				side: "left-top",
			},
			{ x: x - r - 12 - labelWidth, y: y, side: "left-bottom" },

			// Top positions
			{ x: x - labelWidth / 2, y: y - r - 12 - labelHeight, side: "top" },
			{
				x: x - labelWidth,
				y: y - r - 12 - labelHeight,
				side: "top-left",
			},
			{ x: x, y: y - r - 12 - labelHeight, side: "top-right" },

			// Bottom positions
			{ x: x - labelWidth / 2, y: y + r + 12, side: "bottom" },
			{ x: x - labelWidth, y: y + r + 12, side: "bottom-left" },
			{ x: x, y: y + r + 12, side: "bottom-right" },
		];
	}

	// Filter positions that are within canvas bounds
	const validPositions = positions.filter((pos) => {
		return (
			pos.x >= 0 &&
			pos.y >= 0 &&
			pos.x + labelWidth <= canvasWidth &&
			pos.y + labelHeight <= canvasHeight
		);
	});

	// Find first position that doesn't collide with existing labels OR other data points
	for (const pos of validPositions) {
		const newRect = {
			left: pos.x,
			top: pos.y,
			right: pos.x + labelWidth,
			bottom: pos.y + labelHeight,
		};

		let hasCollision = false;

		// Check collision with existing labels
		for (const existingLabel of existingLabels) {
			if (checkCollision(newRect, existingLabel.rect)) {
				hasCollision = true;
				break;
			}
		}

		// Check collision with other data points (bubbles)
		if (!hasCollision && allDataPoints) {
			for (const dataPoint of allDataPoints) {
				const { x: pointX, y: pointY, r: pointR } = dataPoint;

				// Create a rectangle representing the bubble area
				const bubbleRect = {
					left: pointX - pointR,
					top: pointY - pointR,
					right: pointX + pointR,
					bottom: pointY + pointR,
				};

				if (checkCollision(newRect, bubbleRect, 5)) {
					// 5px padding around bubbles
					hasCollision = true;
					break;
				}
			}
		}

		if (!hasCollision) {
			return pos;
		}
	}

	// If no position found, return the first valid position (fallback)
	return validPositions[0] || positions[0];
}
const PIE_CHART_ID = "pie-chart";
const LINE_CHART_ID = "line-chart";
const COMBO_CHART_ID = "combo-chart";
const BUBBLE_CHART_ID = "bubble-chart";

window.select2s = [];
window.rangeDatePickers = [];

const chart = {
	createSelect2: () => {
		const select2JS = appendScripts([
			"https://cdn.jsdelivr.net/npm/select2@4.1.0-rc.0/dist/js/select2.min.js",
		]);
		const select2CSS = appendStyles([
			"https://cdn.jsdelivr.net/npm/select2@4.1.0-rc.0/dist/css/select2.min.css",
		]);

		select2JS.then((script) => {
			if (!window.select2s) {
				window.select2s = [];
			}

			const selects = $(".custom-select");
			selects.each((index, select) => {
				const selectElement = $(select).find("select");

				if (!selectElement.length) {
					return;
				}

				try {
					// Check if this select is already initialized
					const existingSelect2 = window.select2s.find(
						(item) => item.select[0] === selectElement[0]
					);

					if (existingSelect2) {
						// Already initialized, skip
						// console.log(
						// 	"Select2 already initialized for:",
						// 	selectElement
						// );
						return;
					}

					if (selectElement.hasClass("select2-hidden-accessible")) {
						// console.log(
						// 	"Select2 already exists (via class check):",
						// 	selectElement
						// );
						return;
					}

					const select2Instance = selectElement.select2();

					window.select2s.push({
						select: selectElement,
						select2: select2Instance,
					});

					// console.log("Select2 initialized for:", selectElement);
				} catch (error) {
					console.log("Error initializing Select2:", error);
				}
			});
		});
	},
	createDatePicker: () => {
		const calendarJS = appendScripts([
			"https://cdn.jsdelivr.net/npm/vanilla-calendar-pro/index.js",
		]);
		const calendarCSS = appendStyles([
			"https://cdn.jsdelivr.net/npm/vanilla-calendar-pro/styles/index.css",
		]);
		const rangeDatePickers = [];
		calendarJS.then((script) => {
			const { Calendar } = window.VanillaCalendarPro;
			const customRangeDates = $(".custom-date-range");
			customRangeDates.each((index, rangeDate) => {
				const dateInput = $(rangeDate).find(".input-date-picker");
				const isDateRangeFilter =
					$(rangeDate).hasClass("date-range-filter");
				if (dateInput.length === 0 || dateInput.length !== 2) return;
				const [startInput, endInput] = dateInput;
				// const locale = {
				// 	months: {
				// 		short: [
				// 			"Thg 1",
				// 			"Thg 2",
				// 			"Thg 3",
				// 			"Thg 4",
				// 			"Thg 5",
				// 			"Thg 6",
				// 			"Thg 7",
				// 			"Thg 8",
				// 			"Thg 9",
				// 			"Thg 10",
				// 			"Thg 11",
				// 			"Thg 12",
				// 		],
				// 		long: [
				// 			"Tháng 1",
				// 			"Tháng 2",
				// 			"Tháng 3",
				// 			"Tháng 4",
				// 			"Tháng 5",
				// 			"Tháng 6",
				// 			"Tháng 7",
				// 			"Tháng 8",
				// 			"Tháng 9",
				// 			"Tháng 10",
				// 			"Tháng 11",
				// 			"Tháng 12",
				// 		],
				// 	},
				// 	weekdays: {
				// 		short: ["CN", "T2", "T3", "T4", "T5", "T6", "T7"],
				// 		long: [
				// 			"Chủ nhật",
				// 			"Thứ 2",
				// 			"Thứ 3",
				// 			"Thứ 4",
				// 			"Thứ 5",
				// 			"Thứ 6",
				// 			"Thứ 7",
				// 		],
				// 	},
				// };
				const options = {
					inputMode: true,
					type: "multiple",
					displayMonthsCount: 2,
					monthsToSwitch: 1,
					selectionDatesMode: "multiple-ranged",
					selectedTheme: "dark",
					onChangeToInput(self) {
						const [startDate, endDate] = self.context.selectedDates;

						if (!self.context.inputElement) return;
						// const inputs  = dateInput
						if (startDate && endDate) {
							startInput.value = formatDate(startDate);
							endInput.value = formatDate(endDate);
							startInput.dataset.date = startDate;
							endInput.dataset.date = endDate;
							$(startInput).trigger("change");
							$(endInput).trigger("change");
							if (isDateRangeFilter) {
								const startUTC = new Date(startDate).setHours(
									TIMEZONE_OFFSET_HOURS,
									0,
									0,
									0
								);
								const endUTC = new Date(endDate).setHours(
									23,
									59,
									59,
									999
								);
								chart.filterLineChart(
									new Date(startUTC),
									new Date(endUTC)
								);
							}
						} else {
							startInput.value = "";
							endInput.value = "";
							startInput.dataset.date = "";
							endInput.dataset.date = "";
							$(startInput).trigger("change");
							$(endInput).trigger("change");
						}
						$(".range-nav li").removeClass("active");
					},
					onUpdate(self) {
						const [startDate, endDate] = self.context.selectedDates;
						console.log("🟩 ~ onUpdate ~ startDate:", startDate);

						if (!self.context.inputElement) return;
						// const inputs  = dateInput

						if (startDate && endDate) {
							startInput.value = formatDate(startDate);
							endInput.value = formatDate(endDate);
							startInput.dataset.date = startDate;
							endInput.dataset.date = endDate;
						} else {
							startInput.value = "";
							endInput.value = "";
							startInput.dataset.date = "";
							endInput.dataset.date = "";
						}
						// console.log(startDate, endDate);
					},
					onInit(self) {
						const handleClickMainElement = (e) => {
							if (e.target.closest("#btn-close")) {
								self.hide();
							}
						};
						self.context.mainElement.addEventListener(
							"click",
							handleClickMainElement
						);
						return () =>
							self.context.mainElement.removeEventListener(
								"click",
								handleClickMainElement
							);
					},
					layouts: {
						default: `
						<div class="vc-header" data-vc="header" role="toolbar" aria-label="Calendar Navigation">
						  <#ArrowPrev />
						  <div class="vc-header__content" data-vc-header="content">
							<#Month />
							<#Year />
						  </div>
						  <#ArrowNext />
						</div>
						<div class="vc-wrapper" data-vc="wrapper">
						  <#WeekNumbers />
						  <div class="vc-content" data-vc="content">
							<#Week />
							<#Dates />
							<#DateRangeTooltip />
						  </div>
						</div>
						<#ControlTime />
						<button id="btn-close" type="button">Close</button>
					  `,
					},
					locale: navigator.language || "vi-VN",
				};
				const calendar = new Calendar(dateInput.get(0), options);
				calendar.init();
				calendar.show();
				rangeDatePickers.push(calendar);
				setTimeout(() => {
					calendar.hide();
				}, 50);
				rangeDate.addEventListener("click", (e) => {
					calendar.show();
					console.log(calendar);
				});
			});
		});
		window.rangeDatePickers = rangeDatePickers;
	},
	updateDatePickerOnChartChange: () => {
		const { lineChart } = chart.getLineChartData();
		const { x } = lineChart.scales;
		const { min, max } = x;
		if (
			window.rangeDatePickers &&
			typeof window.rangeDatePickers === "object"
		) {
			requestAnimationFrame(() => {
				window.rangeDatePickers.forEach((picker) => {
					const isDatePickerFilter =
						picker.context.inputElement.closest(
							".date-range-filter"
						);
					if (isDatePickerFilter) {
						const startMonth = new Date(min).getMonth();
						const startYear = new Date(min).getFullYear();
						// picker.selectedDates = [min, max];
						picker.selectedDates = [
							new Date(min).toISOString().split("T")[0],
							new Date(max).toISOString().split("T")[0],
						];
						// console.log(new Date(min).toISOString().split("T")[0]);
						const startDateUTC = new Date(min);
						picker.selectedMonth = startDateUTC.getUTCMonth();
						picker.selectedYear = startDateUTC.getUTCFullYear();
						// picker.selectedMonth = startMonth;
						// picker.selectedYear = startYear;
						picker.update({
							dates: true,
						});
					}
				});
			});
		}
	},
	implementChartJS: () => {
		const chartJSScript = appendScripts([
			"https://cdn.jsdelivr.net/npm/chart.js@4.5.0/dist/chart.umd.min.js",
		]);
		chartJSScript.then((script) => {
			appendScripts([
				"https://cdn.jsdelivr.net/npm/chartjs-adapter-date-fns@3.0/dist/chartjs-adapter-date-fns.bundle.min.js",
			]).then(() => {
				chart.createPieChart();
				// FE.chart.updatePieChartData([
				// 	{
				// 		label: "VRE",
				// 		value: 40,
				// 	},
				// 	{
				// 		label: "VRE",
				// 		value: 50,
				// 	},
				// 	{
				// 		label: "VRE",
				// 		value: 60,
				// 	},
				// 	{
				// 		label: "MBB",
				// 		value: 10,
				// 	},
				// 	{
				// 		label: "VRE",
				// 		value: 20,
				// 	},
				// 	{
				// 		label: "VRE",
				// 		value: 30,
				// 	},
				// ]);
				appendScripts([
					"https://cdn.jsdelivr.net/npm/hammerjs@2.0.8",
				]).then(() => {
					appendScripts([
						"https://cdn.jsdelivr.net/npm/chartjs-plugin-zoom@2.2.0/dist/chartjs-plugin-zoom.min.js",
						"https://cdn.jsdelivr.net/npm/chartjs-plugin-datalabels@2.0.0",
					]).then(() => {
						chart.createLineChart();
						chart.handleRangeButtonClick();
						//
						chart.createComboChart();
						// chart.randomComboChartData();
						//
						chart.createBubbleChart();

						const allScriptLoadedEvent = new CustomEvent(
							"all-script-loaded"
						);
						document.body.dispatchEvent(allScriptLoadedEvent);
					});
				});
			});
		});
	},
	createPieChart: (data) => {
		const pieChartElement = document.querySelector(".pie-chart");
		if (!pieChartElement) return;
		const canvas = document.createElement("canvas");
		canvas.id = PIE_CHART_ID;
		pieChartElement.appendChild(canvas);
		const ctx = canvas.getContext("2d");

		let top_3_largest_value = [];
		const pieChart = new Chart(ctx, {
			type: "pie",
			data: {
				// labels: ["Loading..."],
				labels: ["MBB", "PVS", "DCM", "DBC", "PVT", "Tiền", "QNS"],
				datasets: [
					{
						// data: [21.67, 20, 17, 12, 11, 11, 9],
						data: [26, 22, 11, 11, 10.41, 10, 8],
						backgroundColor: [
							"#e63946",
							"#f77f00",
							"#ffd60a",
							"#2ecc71",
							"#27ae60",
							"#1d4ed8",
							"#0096c7",
							"#9b5de5",
							"#6a4c93",
							"#ff61a6",
							"#d81159",
							"#8d5524",
							"#ff914d",
							"#4bc0c8",
							"#f1f1f1",
						],
						hoverOffset: 0,
					},
				],
				// [
				// 	{
				// 		data: [0],
				// 		backgroundColor: [
				// 			chartColors[0],
				// 			"#278400",
				// 			"#00996C",
				// 			"#2355CB",
				// 			"#9923CB",
				// 			"#CB2355",
				// 		],
				// 		hoverOffset: 0,
				// 	},
				// ],
			},
			plugins: [
				{
					afterInit: (chart) => {
						top_3_largest_value = getTopValue(chart, 3);
					},
					afterUpdate: (chart) => {
						top_3_largest_value = getTopValue(chart, 3);
					},
					afterDraw: (chart) => {
						const {
							ctx,
							data,
							chartArea: { width, height },
						} = chart;
						chart.getDatasetMeta(0).data.forEach((arc, i) => {
							const { x, y } = arc.tooltipPosition();
							const halfWidth = width / 2;
							const halfHeight = height / 2;
							const value = data.datasets[0].data[i];
							const label = data.labels[i];
							// const isFirstData = i === 0;

							const color = data.datasets[0].backgroundColor[i];
							ctx.strokeStyle = color;

							const newText = document.createElement("div");
							newText.className = `pie-chart-value pie-chart-value-${i} placement-${
								x >= halfWidth ? "right" : "left"
							}`;
							newText.innerHTML = `<div class="pie-chart-value-template">
                        <div class="label">${label}</div>
                        <div class="value">${formatNumber(value, "%")}</div>
						</div>`;
							newText.style.whiteSpace = "nowrap";
							newText.style.position = "absolute";
							$(pieChartElement)
								.find(`.pie-chart-value-${i}`)
								.remove();
							const isInTopSelectedValue =
								top_3_largest_value.includes(value);
							if (label && value && isInTopSelectedValue) {
								$(pieChartElement).append(newText);
							}
							if (isInTopSelectedValue) {
								arc.isShowOnChart = true;
							} else {
								arc.isShowOnChart = false;
							}
							// newText.style.left = `${
							// 	x >= halfWidth
							// 		? x + 6
							// 		: x - 6 - newText.offsetWidth / 2
							// }px`;
							newText.style.left = `${
								x - newText.offsetWidth / 2
							}px`;
							newText.style.top = `${
								y - newText.offsetHeight / 2
							}px`;

							newText.style.color = "white";
							if (arc.active) {
								newText.classList.add("active");
							} else {
								newText.classList.remove("active");
							}
						});
					},
				},
			],
			options: {
				datasets: {
					pie: {
						borderWidth: 0,
					},
				},
				responsive: true,
				plugins: {
					legend: {
						display: false,
					},
					tooltip: {
						enabled: false,
						external: function (context) {
							const tooltipID = `chartjs-tooltip-${PIE_CHART_ID}`;
							let tooltipEl = document.getElementById(tooltipID);

							if (!tooltipEl) {
								tooltipEl = document.createElement("div");
								tooltipEl.id = tooltipID;
								tooltipEl.innerHTML =
									"<div class='piechart-tooltip'></div>";
								document.body.appendChild(tooltipEl);
							}

							const tooltipModel = context.tooltip;
							if (tooltipModel.opacity === 0) {
								tooltipEl.style.opacity = 0;
								return;
							}

							tooltipEl.classList.remove(
								"above",
								"below",
								"no-transform"
							);
							if (tooltipModel.yAlign) {
								tooltipEl.classList.add(tooltipModel.yAlign);
							} else {
								tooltipEl.classList.add("no-transform");
							}

							function getBody(bodyItem) {
								return bodyItem.lines;
							}

							const colors =
								tooltipModel.labelColors[0].backgroundColor;

							if (tooltipModel.body) {
								const titleLines = tooltipModel.title || [];
								const bodyLines =
									tooltipModel.body.map(getBody);

								let innerHtml = "";
								innerHtml += `<div class="piechart-tooltip-color" style="background-color: ${colors}"></div>`;
								titleLines.forEach(function (title) {
									innerHtml += `<div class="piechart-tooltip-title">${title}</div>`;
								});

								bodyLines.forEach(function (body, i) {
									const value = tooltipModel.dataPoints[i]?.raw || 0;
									innerHtml += `<div class="piechart-tooltip-content">(${formatNumber(
										value,
										"%"
									)})</div>`;
								});
								innerHtml += "</div>";

								let tableRoot =
									tooltipEl.querySelector(
										".piechart-tooltip"
									);
								tableRoot.innerHTML = innerHtml;
							}

							const position =
								context.chart.canvas.getBoundingClientRect();
							tooltipModel.chart
								.getDatasetMeta(0)
								.data.forEach((arc, i) => {
									const isShowOnChart =
										arc.isShowOnChart ?? false;
									if (arc.active && isShowOnChart) {
										tooltipEl.style.opacity = 0;
									}
									if (arc.active && !isShowOnChart) {
										tooltipEl.style.opacity = 1;
									}
								});
							tooltipEl.style.position = "absolute";
							tooltipEl.style.left =
								position.left +
								window.scrollX +
								tooltipModel.caretX -
								tooltipEl.offsetWidth +
								"px";
							tooltipEl.style.top =
								position.top +
								window.scrollY +
								tooltipModel.caretY -
								tooltipEl.offsetHeight +
								"px";
							tooltipEl.style.pointerEvents = "none";
						},
					},
				},
			},
		});
	},
	updatePieChartData: (data = []) => {
		const labels = data.map((item) => item.label);
		const datasets = data.map((item) => item.value);
		// Clean all data
		const pieChart = Chart.getChart(PIE_CHART_ID);
		if (!pieChart) {
			console.error("Pie chart is not initialized");
			return;
		}
		pieChart.data.labels = [];
		pieChart.data.datasets[0].data = [];
		// Update data
		pieChart.data.labels = labels;
		pieChart.data.datasets[0].data = datasets;
		pieChart.update();
		// Remove all text
		const pieChartElement = pieChart.canvas.closest(".pie-chart-wrapper");
		const allText = pieChartElement.querySelectorAll(".pie-chart-value");
		const loadingOverlay =
			pieChartElement.querySelector(".loading-overlay");
		if (loadingOverlay) {
			loadingOverlay.remove();
		}
		allText.forEach((text) => {
			text.remove();
		});
	},
	createLineChart: () => {
		const lineChartElement = document.querySelector(".line-chart");
		if (!lineChartElement) return;
		const canvas = document.createElement("canvas");
		canvas.id = LINE_CHART_ID;
		canvas.width = lineChartElement.offsetWidth;
		canvas.height = lineChartElement.offsetHeight;
		lineChartElement.appendChild(canvas);
		const ctx = canvas.getContext("2d");
		const lineChart = new Chart(ctx, {
			type: "line",
			data: {
				datasets: [
					{
						label: "ALPHA A",
						data: [
							// {
							// 	x: new Date(),
							// 	y: 20,
							// 	price: 10000,
							// },
							// {
							// 	x: new Date().setDate(new Date().getDate() - 1),
							// 	y: 10,
							// 	price: 50000,
							// },
						],
						...chartLineStyles[0],
					},
					{
						label: "VN-INDEX",
						data: [
							// {
							// 	x: new Date().setDate(new Date().getDate() - 1),
							// 	y: 10,
							// 	price: 1000,
							// },
							// {
							// 	x: new Date().setDate(new Date().getDate() - 2),
							// 	y: 0,
							// 	price: 2000,
							// },
						],
						...chartLineStyles[1],
					},
				],
			},
			plugins: [
				{
					afterDraw: (currentChart) => {
						// chart.handleRangeButtonVisibility();
					},
					afterUpdate: () => {
						// chart.handleRangeButtonVisibility();
					},
				},
			],
			options: {
				interaction: {
					// mode: "index",
					mode: "nearest",
					// intersect: false,
				},
				plugins: {
					legend: {
						display: false,
					},
					tooltip: {
						enabled: false, // Disable default tooltip
						position: "nearest", // Position tooltip near the cursor
						intersect: false,
						external: function (context) {
							// Tooltip Element
							let tooltipEl =
								document.getElementById("chartjs-tooltip");

							// Create element on first render
							if (!tooltipEl) {
								tooltipEl = document.createElement("div");
								tooltipEl.id = "chartjs-tooltip";
								tooltipEl.className =
									"absolute line-chart-tooltip";
								tooltipEl.style.pointerEvents = "none";
								tooltipEl.style.transition = "all .1s ease";
								lineChartElement.appendChild(tooltipEl);
							}

							// Hide if no tooltip
							const tooltipModel = context.tooltip;
							const color = tooltipModel.dataPoints
								? tooltipModel.dataPoints[0].dataset
										.backgroundColor
								: chartLineStyles[0].backgroundColor;
							if (tooltipModel.opacity === 0) {
								tooltipEl.style.opacity = 0;
								tooltipEl.classList.remove("active");
								return;
							}

							// Set caret Position
							tooltipEl.classList.remove(
								"above",
								"below",
								"no-transform",
								"center",
								"top",
								"left",
								"right",
								"bottom"
							);
							if (tooltipModel.yAlign) {
								tooltipEl.classList.add(tooltipModel.yAlign);
							} else {
								tooltipEl.classList.add("no-transform");
							}

							// Set Text
							if (tooltipModel.body) {
								const titleLines = tooltipModel.title || [];

								const bodyLines = tooltipModel.body.map(
									(b) => b.lines
								);

								let innerHtml = "";

								// Date (Title)
								// titleLines.forEach(function (title) {
								// 	innerHtml += `<div class="tooltip-title">${title}</div>`;
								// });

								// VN-Index and Percentage (Body)

								const interactionType =
									context.chart.options.interaction.mode;
								bodyLines.forEach(function (body, i) {
									const labelIndex =
										interactionType === "nearest" ? 0 : i;
									const value =
										tooltipModel.dataPoints[i].raw;
									const nearrestLabel =
										body[labelIndex]?.split(":")[0] || "";
									const label =
										interactionType === "nearest"
											? nearrestLabel
											: context.chart.data.datasets[
													labelIndex
											  ].label || "";

									const { x, y, ...rest } = value;

									const price = rest.price || 0;
									const xDate = new Date(x).toISOString();
									// console.log("🟩 ~ x:", x);
									// console.log("🟩 ~ xDate:", xDate);
									innerHtml += `<div class='tooltip-title tooltip-title-${i}'>${formatDate(
										xDate
									)}</div><div class="tooltip-content">${label}: ${formatNumber(
										price
									)} | ${formatNumber(y, "%")}</div>`;
									// console.log(
									// 	"🟩 ~ label:",
									// 	context.chart.data.datasets
									// );
								});

								tooltipEl.innerHTML = innerHtml;
							}

							// Display, position, and set styles for font
							const isTooltipOnRight =
								tooltipModel.caretX >
								lineChartElement.offsetWidth -
									tooltipEl.offsetWidth;
							tooltipEl.classList.add(
								isTooltipOnRight ? "right" : "left"
							);
							tooltipEl.style.opacity = 1;
							tooltipEl.classList.add("active");
							const arrowWidth = 12;
							tooltipEl.style.left = isTooltipOnRight
								? tooltipModel.caretX -
								  tooltipEl.offsetWidth -
								  arrowWidth +
								  "px"
								: tooltipModel.caretX + arrowWidth + "px";

							tooltipEl.style.top =
								tooltipModel.caretY -
								tooltipEl.offsetHeight / 2 +
								"px";
							tooltipEl.style.setProperty("--color", color);
						},
					},
					zoom: {
						pan: {
							enabled: false,
							onPan: ({ trigger }) => {
								console.log("🟩 ~ trigger:", trigger);

								chart.updateDatePickerOnChartChange();
								if (trigger === "api") return;
								inActiveAllRangeNav();
							},
						},
						limits: {
							// y: {
							// 	min: "original",
							// 	max: "original",
							// },
							y: {
								min: "original",
								// max: "original",
								max: "original",
							},
							x: {
								min: "original",
								max: "original",
							},
						},
						zoom: {
							mode: "xy",
							wheel: {
								enabled: false,
							},
							pinch: {
								enabled: false,
							},
							onZoom: ({ trigger }) => {
								console.log("🟩 ~ trigger:", trigger);
								chart.updateDatePickerOnChartChange();
								if (trigger === "api") return;
								inActiveAllRangeNav();
							},
						},
					},
				},
				ticks: {
					font: {
						size: fontSize(16),
						weight: 400,
						family: "Inter Display",
					},
				},
				scales: {
					x: {
						type: "time",
						time: {
							unit: "month", //Remove unit for dynamic time display
							displayFormats: {
								month: "MM/yyyy",
								day: "dd/MM/yyyy",
							},
						},
						border: {
							width: 2,
							color: "rgba(256, 256, 256, 0.5)",
						},
						grid: {
							color: "rgba(256, 256, 256, 0.1)",
							drawTicks: false,
						},
						ticks: {
							padding: 20,
							beginAtZero: true,
							color: "#fff",
						},
					},
					y: {
						border: {
							width: 2,
							color: "rgba(256, 256, 256, 0.5)",
						},
						grid: {
							color: "rgba(256, 256, 256, 0.3)",
							// drawTicks: false,
							tickColor: "transparent",
						},
						ticks: {
							stepSize: 10,
							beginAtZero: true,
							color: chartColors[0],
							callback: (value) => {
								return formatNumber(value, "%");
							},
						},
					},
				},
			},
		});

		function inActiveAllRangeNav() {
			$(".range-nav li").removeClass("active");
		}
		if (isLocal()) {
			chart.randomLineChartData();
		}
	},
	handleRangeButtonClick: () => {
		const lineChart = Chart.getChart(LINE_CHART_ID);
		if (!lineChart) {
			console.error("Line chart is not initialized");
			return;
		}
		$(document).on("click", ".range-nav a", (e) => {
			const id = $(e.target).attr("id");
			if (!id) {
				console.error(`Id not found in range-nav a, ${e.target}`);
				return;
			}
			const li = $(e.target).closest("li");
			li.siblings().removeClass("active");
			li.addClass("active");
			$(e.target).closest(".range-nav ul").scrollTo(li, 300);
			const startDate = li.attr("data-start-date")
				? new Date(li.attr("data-start-date"))
				: new Date();
			const endDate = li.attr("data-end-date")
				? new Date($(e.target).closest("li").attr("data-end-date"))
				: new Date();
			chart.filterLineChart(startDate, endDate);
		});
	},
	filterLineChart: (startDate, endDate) => {
		const { lineChart, datasets } = chart.getLineChartData();
		const filteredDatasets = datasets.filter((dataset) => {
			const filteredData = dataset.data.filter((item) => {
				const { x } = item;
				if (!x) return false;
				return new Date(x) >= startDate && new Date(x) <= endDate;
			});
			return { ...dataset, data: filteredData };
		});
		const [minY, maxY] = [
			Math.min(
				...filteredDatasets.map((dataset) =>
					Math.min(...dataset.data.map((item) => item.y))
				)
			),
			Math.max(
				...filteredDatasets.map((dataset) =>
					Math.max(...dataset.data.map((item) => item.y))
				)
			),
		];
		const newStartDate = new Date(startDate);
		newStartDate.setHours(TIMEZONE_OFFSET_HOURS, 0, 0, 0);
		const [minX, maxX] = [newStartDate.getTime(), endDate.getTime()];

		chart.setLineScale({
			x: { min: minX, max: maxX },
			y: { min: minY, max: maxY },
		});
		lineChart.update();
	},
	setLineScale: ({ x: { min, max }, y: { min: minY, max: maxY } }) => {
		const { lineChart } = chart.getLineChartData();
		const customZoomStart = new CustomEvent("onZoomStart", {
			detail: {
				chart: lineChart,
				x: { min, max },
				y: { min: minY, max: maxY },
			},
		});
		document.body.dispatchEvent(customZoomStart);
		lineChart.zoomScale("x", { min, max }, "default");
		lineChart.zoomScale("y", { min: minY, max: maxY }, "default");
		// lineChart.options.plugins.zoom.limits.x.max = max;
		// lineChart.options.plugins.zoom.limits.x.min = min;
		// lineChart.options.plugins.zoom.limits.y.max = maxY;
		// lineChart.options.plugins.zoom.limits.y.min = minY;
		lineChart.update();
		const customEvent = new CustomEvent("onZoomComplete", {
			detail: {
				chart: lineChart,
				x: { min, max },
				y: { min: minY, max: maxY },
			},
		});
		document.body.dispatchEvent(customEvent);
	},
	// handleRangeButtonVisibility: () => {
	// 	const { datasets } = chart.getLineChartData();
	// 	let allDates = [];
	// 	datasets.forEach((dataset) => {
	// 		const { data: dataSet } = dataset;
	// 		if (!dataSet.length) return;
	// 		dataSet.forEach((item) => {
	// 			const { x } = item;
	// 			if (x) {
	// 				allDates.push(new Date(x));
	// 			}
	// 		});
	// 	});
	// 	if (allDates.length === 0) return;

	// 	const datasetsMaxDate = new Date(Math.max(...allDates)).setHours(
	// 		23,
	// 		59,
	// 		59,
	// 		999
	// 	);
	// 	const datasetsMinDate = new Date(Math.min(...allDates)).setHours(
	// 		TIMEZONE_OFFSET_HOURS,
	// 		0,
	// 		0,
	// 		0
	// 	);

	// 	$(".range-nav a").each(function () {
	// 		const id = $(this).attr("id");
	// 		if (!id) return;
	// 		let currentEndDate = new Date(datasetsMaxDate);
	// 		let currentStartDate = new Date(currentEndDate.getFullYear(), 0, 1);

	// 		switch (id) {
	// 			case "YTD":
	// 				currentStartDate = new Date(
	// 					currentEndDate.getFullYear(),
	// 					0,
	// 					1
	// 				);
	// 				break;
	// 			case "1-month":
	// 				currentStartDate = new Date(currentEndDate);
	// 				currentStartDate.setMonth(currentStartDate.getMonth() - 1);
	// 				break;
	// 			case "3-month":
	// 				currentStartDate = new Date(currentEndDate);
	// 				currentStartDate.setMonth(currentStartDate.getMonth() - 3);
	// 				break;
	// 			case "6-month":
	// 				currentStartDate = new Date(currentEndDate);
	// 				currentStartDate.setMonth(currentStartDate.getMonth() - 6);
	// 				break;
	// 			case "1-year":
	// 				currentStartDate = new Date(currentEndDate);
	// 				currentStartDate.setFullYear(
	// 					currentStartDate.getFullYear() - 1
	// 				);
	// 				break;
	// 			case "3-year":
	// 				currentStartDate = new Date(currentEndDate);
	// 				currentStartDate.setFullYear(
	// 					currentStartDate.getFullYear() - 3
	// 				);
	// 				break;
	// 			case "5-year":
	// 				currentStartDate = new Date(currentEndDate);
	// 				currentStartDate.setFullYear(
	// 					currentStartDate.getFullYear() - 5
	// 				);
	// 				break;
	// 			case "from-begin":
	// 				currentEndDate = new Date(datasetsMaxDate);
	// 				currentStartDate = new Date(datasetsMinDate);
	// 				break;
	// 			default:
	// 				break;
	// 		}

	// 		const isVisible =
	// 			currentStartDate.getTime() >= datasetsMinDate &&
	// 			currentEndDate.getTime() <= datasetsMaxDate;
	// 		const li = $(this).closest("li");

	// 		li.attr("data-start-date", currentStartDate);
	// 		li.attr("data-end-date", currentEndDate);
	// 		li.toggleClass("disabled", !isVisible);
	// 	});
	// },
	handleRangeButtonVisibility: () => {
		const { datasets } = chart.getLineChartData();
		if (!datasets || datasets.length === 0) return;

		const allDates = datasets.flatMap(
			(dataset) => dataset.data?.map((item) => new Date(item.x)) ?? []
		);

		if (allDates.length === 0) {
			$(".range-nav li").addClass("disabled");
			return;
		}

		const allTimestamps = allDates.map((d) => d.getTime());
		const datasetsMaxDate = new Date(Math.max(...allTimestamps));
		const datasetsMinDate = new Date(Math.min(...allTimestamps));

		datasetsMinDate.setHours(TIMEZONE_OFFSET_HOURS, 0, 0, 0);
		datasetsMaxDate.setHours(23, 59, 59, 999);

		const availableDateStrings = new Set(
			allDates.map((d) => {
				const year = d.getUTCFullYear();
				const month = String(d.getUTCMonth() + 1).padStart(2, "0");
				const day = String(d.getUTCDate()).padStart(2, "0");
				return `${year}-${month}-${day}`;
			})
		);

		$(".range-nav a").each(function () {
			const id = $(this).attr("id");
			if (!id) return;

			// 1. DEFINE THE SEARCH LIMIT
			// These are the only buttons that will have a limited backward search.
			const limitedSearchButtons = ["1-month", "3-month", "5-month"];
			const hasSearchLimit = limitedSearchButtons.includes(id);
			const SEARCH_LIMIT_DAYS = 10;

			const li = $(this).closest("li");
			const endDate = new Date(datasetsMaxDate);
			let theoreticalStartDate = new Date(endDate);

			switch (id) {
				case "YTD":
					theoreticalStartDate.setFullYear(
						endDate.getFullYear(),
						0,
						1
					);
					break;
				case "1-month":
					theoreticalStartDate.setMonth(
						theoreticalStartDate.getMonth() - 1
					);
					break;
				case "3-month":
					theoreticalStartDate.setMonth(
						theoreticalStartDate.getMonth() - 3
					);
					break;
				case "5-month":
					theoreticalStartDate.setMonth(
						theoreticalStartDate.getMonth() - 5
					);
					break;
				case "6-month":
					theoreticalStartDate.setMonth(
						theoreticalStartDate.getMonth() - 6
					);
					break;
				case "1-year":
					theoreticalStartDate.setFullYear(
						theoreticalStartDate.getFullYear() - 1
					);
					break;
				case "3-year":
					theoreticalStartDate.setFullYear(
						theoreticalStartDate.getFullYear() - 3
					);
					break;
				case "5-year":
					theoreticalStartDate.setFullYear(
						theoreticalStartDate.getFullYear() - 5
					);
					break;
				case "from-begin":
					theoreticalStartDate = new Date(datasetsMinDate);
					break;
				default:
					return;
			}

			let actualStartDate = new Date(theoreticalStartDate);
			let dataFound = false;
			let daysSearchedBack = 0;

			while (actualStartDate.getTime() >= datasetsMinDate.getTime()) {
				if (hasSearchLimit && daysSearchedBack >= SEARCH_LIMIT_DAYS) {
					break;
				}

				const dateString = [
					actualStartDate.getUTCFullYear(),
					String(actualStartDate.getUTCMonth() + 1).padStart(2, "0"),
					String(actualStartDate.getUTCDate()).padStart(2, "0"),
				].join("-");

				if (availableDateStrings.has(dateString)) {
					dataFound = true;
					break;
				}

				actualStartDate.setDate(actualStartDate.getDate() - 1);
				actualStartDate.setHours(TIMEZONE_OFFSET_HOURS, 0, 0, 0);
				daysSearchedBack++; // Increment the counter after each failed day
			}

			const isVisible = dataFound;
			li.toggleClass("disabled", !isVisible);

			if (isVisible) {
				li.attr("data-start-date", actualStartDate.toISOString());
			} else {
				li.attr("data-start-date", theoreticalStartDate.toISOString());
			}
			li.attr("data-end-date", endDate.toISOString());
		});
	},
	getLineChartData: () => {
		const lineChart = Chart.getChart(LINE_CHART_ID);
		if (!lineChart) {
			console.error("Cannot get line chart data");
			return;
		}
		const { data } = lineChart;
		const { datasets } = data;
		return {
			lineChart,
			datasets,
		};
	},
	updateLineChartData: (data = []) => {
		const { lineChart } = chart.getLineChartData();
		lineChart.data.datasets.length = 0;
		data.forEach((dataset, index) => {
			lineChart.data.datasets.push({
				label: dataset.label,
				data: dataset.data,
				...chartLineStyles[index % 2],
			});
		});
		lineChart.update();
		$(".alpha-2 .loading-overlay").remove();
	},
	exampleLineChartData: () => {
		const data = [
			{
				label: "Demo 1",
				data: [
					{
						x: new Date().setDate(new Date().getDate() - 1),
						y: 0,
						price: 3000,
					},
					{
						x: new Date(),
						y: 100,
						price: 10000,
					},
				],
			},
			{
				label: "Demo 2",
				data: [
					{
						x: new Date().setDate(new Date().getDate() - 1),
						y: 100,
						price: 10000,
					},
					{
						x: new Date(),
						y: 0,
						price: 2000,
					},
				],
			},
		];
		console.log(data);
	},
	randomLineChartData: () => {
		const randomData = Array.from({ length: 10 }, () => {
			return {
				label: `Random title ${Math.random()}`,
				data: Array.from({ length: 10 }, () => {
					return {
						x: new Date(
							new Date().getFullYear() -
								Math.floor(Math.random() * 10), // Random year within last 10 years
							Math.floor(Math.random() * 6), // Random month (0-11)
							Math.floor(Math.random() * 28) + 1 // Random day (1-28)
						).getTime(),
						y: Math.random() * 70,
						price: Math.random() * 10000,
					};
				}),
			};
		});
		// Sort data points inside each dataset by x value
		randomData.forEach((dataset) => {
			dataset.data.sort((a, b) => new Date(a.x) - new Date(b.x));
		});
		// Optionally, sort datasets by the first x value
		const sortData = randomData.sort((a, b) => {
			return new Date(a.data[0].x) - new Date(b.data[0].x);
		});
		chart.updateLineChartData(sortData);
	},
	createComboChart() {
		const demoLabels = [];
		const data = {
			labels: demoLabels,
			datasets: [
				{
					label: "Demo - GTĐT",
					data: [],
					unit: "Tỷ VNĐ",
					...chartComboStyle[0],
				},
				{
					label: "Demo - TTCP",
					data: [],
					unit: "%",
					...chartComboStyle[1],
				},
			],
		};
		const getOrCreateLegendList = (chart, id) => {
			let legendContainer = document.getElementById(id);

			if (!legendContainer) {
				legendContainer = document.createElement("div");
				legendContainer.id = id;
				legendContainer.classList.add("custom-legend-container");
				wrapElement.append(legendContainer);
			}

			let listContainer = legendContainer.querySelector("ul");

			if (!listContainer) {
				listContainer = document.createElement("ul");
				legendContainer.appendChild(listContainer);
			}

			return listContainer;
		};

		const htmlLegendPlugin = {
			id: "htmlLegend",
			afterUpdate(chart, args, options) {
				const ul = getOrCreateLegendList(chart, options.containerID);
				// Remove old legend items
				while (ul.firstChild) {
					ul.firstChild.remove();
				}

				// Reuse the built-in legendItems generator
				const items =
					chart.options.plugins.legend.labels.generateLabels(chart);
				const reverseItems = [...items].reverse();
				reverseItems.forEach((item) => {
					const li = document.createElement("li");
					li.onclick = () => {
						const { type } = chart.config;
						if (type === "pie" || type === "doughnut") {
							chart.toggleDataVisibility(item.index);
						} else {
							chart.setDatasetVisibility(
								item.datasetIndex,
								!chart.isDatasetVisible(item.datasetIndex)
							);
						}
						chart.update();
						const isActive = !chart.isDatasetVisible(
							item.datasetIndex
						);
						if (isActive) {
							li.classList.add("hide-chart");
						} else {
							li.classList.remove("hide-chart");
						}
					};

					const boxSpan = document.createElement("span");
					boxSpan.style.setProperty("--background", item.fillStyle);
					boxSpan.style.setProperty(
						"--border-color",
						item.strokeStyle
					);
					boxSpan.style.setProperty(
						"--border-width",
						item.lineWidth + "px"
					);
					// Text
					const textContainer = document.createElement("p");
					const text = document.createTextNode(item.text);
					textContainer.appendChild(text);

					li.appendChild(boxSpan);
					li.appendChild(textContainer);
					ul.appendChild(li);
				});
			},
		};
		const config = {
			type: "bar",
			data: data,
			plugins: [
				ChartDataLabels,
				htmlLegendPlugin,
				{
					afterInit(chart, args, options) {
						// FE.chart.randomComboChartData();
					},
					afterUpdate(chart, args, options) {
						// // Bound column range on mobile
						const { datasets, labels } = chart.data;
					},
				},
			],
			options: {
				interaction: {
					// mode: "index",
					mode: "nearest",
					intersect: false,
				},
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					htmlLegend: {
						// ID of the container to put the legend in
						containerID: `${COMBO_CHART_ID}-legend-container`,
					},
					legend: {
						display: false,
					},
					tooltip: {
						enabled: false,
						position: "nearest",
						external: function (context) {
							const tooltipID = `chartjs-tooltip-${COMBO_CHART_ID}`;
							let tooltipEl = document.getElementById(tooltipID);
							if (!tooltipEl) {
								tooltipEl = document.createElement("div");
								tooltipEl.id = tooltipID;
								tooltipEl.innerHTML =
									"<div class='combo-chart-tooltip'></div>";
								document.body.appendChild(tooltipEl);
							}

							tooltipEl.classList.add(
								`chartjs-tooltip-${COMBO_CHART_ID}`
							);

							const tooltipModel = context.tooltip;
							const currentDatasetsType =
								tooltipModel.dataPoints[0].dataset.type;
							if (tooltipModel.opacity === 0) {
								tooltipEl.style.opacity = 0;
								return;
							}
							// console.log(tooltipModel);
							tooltipEl.classList.remove(
								"above",
								"below",
								"no-transform",
								"left",
								"right"
							);
							if (tooltipModel.yAlign) {
								tooltipEl.classList.add(tooltipModel.yAlign);
							} else {
								tooltipEl.classList.add("no-transform");
							}

							function getBody(bodyItem) {
								return bodyItem.lines;
							}

							const colors =
								tooltipModel.labelColors[0].backgroundColor;
							const unit =
								tooltipModel.dataPoints[0].dataset.unit;
							if (tooltipModel.body) {
								const titleLines = tooltipModel.title || [];
								const bodyLines =
									tooltipModel.body.map(getBody);

								let innerHtml = "";
								innerHtml += `<div class="combo-chart-tooltip-color" style="background-color: ${colors}"></div>`;
								titleLines.forEach(function (title) {
									innerHtml += `<div class="combo-chart-tooltip-title">${title}</div>`;
								});
								bodyLines.forEach(function (body, i) {
									const value =
										tooltipModel.dataPoints[0].raw;
									const newValue =
										unit === "%"
											? value
											: value * valueRatio;
									innerHtml += `<div class="combo-chart-tooltip-content">${formatNumber(
										newValue,
										unit,
										unit === "%" ? false : true
									)}</div>`;
								});
								innerHtml += "</div>";

								let tableRoot = tooltipEl.querySelector(
									".combo-chart-tooltip"
								);
								tableRoot.innerHTML = innerHtml;
							}

							const position =
								context.chart.canvas.getBoundingClientRect();

							const halfWidth = position.width / 2;

							const x =
								position.left +
								window.scrollX +
								tooltipModel.caretX -
								tooltipEl.offsetWidth;
							tooltipModel.chart
								.getDatasetMeta(0)
								.data.forEach((arc, i) => {
									if (arc.active) {
										tooltipEl.style.opacity = 1;
									}
								});

							if (tooltipModel.caretX < halfWidth) {
								tooltipEl.classList.add("right");
							} else {
								tooltipEl.classList.add("left");
							}

							tooltipEl.style.position = "absolute";
							tooltipEl.style.setProperty("--background", colors);

							tooltipEl.style.left =
								position.left +
								tooltipModel.caretX -
								tooltipEl.offsetWidth +
								"px";
							tooltipEl.style.top =
								position.top +
								window.scrollY +
								tooltipModel.caretY -
								tooltipEl.offsetHeight / 2 +
								"px";
							tooltipEl.style.pointerEvents = "none";
							tooltipEl.style.opacity = 1;
							tooltipEl.style.display = "block";
						},
					},
					datalabels: {
						color: function (context) {
							return context.dataset.backgroundColor;
						},
						font: {
							weight: "bold",
							size: fontSize(14),
						},
						formatter: Math.round,
						padding: 6,
						formatter: function (value, context) {
							const { x, y } = value;
							if (!y) return "";
							return formatNumber(y, "%");
						},
					},
					zoom: {
						pan: {
							enabled: true,
							onPan: ({ chart, trigger }) => {
								// console.log(chart.getZoomedScaleBounds());
								// zoomToolTip().hide();
								// console.log("🟩 ~ trigger:", trigger);
								// chart.updateDatePickerOnChartChange();
								// if (trigger === "api") return;
								// inActiveAllRangeNav();
							},
							onPanComplete: (e) => {
								// console.log(e);
								// zoomToolTip().show();
							},
						},
						limits: {
							y: {
								min: "original",
								max: "original",
							},
							x: {
								min: "original",
								max: "original",
							},
						},
						zoom: {
							mode: "x,y",
							wheel: {
								enabled: false,
							},
							pinch: {
								enabled: false,
							},
							onZoom: ({ trigger }) => {
								console.log(trigger);
								// zoomToolTip().hide();
								// console.log("🟩 ~ trigger:", trigger);
								// chart.updateDatePickerOnChartChange();
								// if (trigger === "api") return;
								// inActiveAllRangeNav();
								$(".chartjs-tooltip-combo-chart").hide();
							},
							onZoomComplete: () => {
								if (
									$(".chartjs-tooltip-combo-chart").length > 0
								) {
									$(".chartjs-tooltip-combo-chart").show();
								}
								// zoomToolTip().show();
							},
						},
					},
				},
				title: {
					font: {
						size: fontSize(16),
						weight: 400,
						family: "Inter Display",
					},
				},
				scales: {
					x: {
						border: {
							width: 2,
							color: "#fff",
						},
						grid: {
							display: false,
							drawTicks: false,
						},
						ticks: {
							padding: 20,
							beginAtZero: true,
							color: "#fff",
							font: {
								size: fontSize(12),
							},
							maxRotation: 45,
							minRotation: 45,
							// callback: (value) => {
							// 	console.log("🟩 ~ value:", value);
							// 	return formatDate(value);
							// },
						},
					},
					y: {
						title: {
							align: "end",
							display: true,
							text: "Giá trị đầu tư: Tỷ VND",
							padding: {
								bottom: 12,
							},
						},
						border: {
							display: false,
						},
						grid: {
							color: "rgba(256, 256, 256, 0.3)",
							// drawTicks: false,
							tickColor: "transparent",
						},
						ticks: {
							// stepSize: 10,
							beginAtZero: true,
							color: "#fff",
							callback: (value) => {
								return formatNumber(value);
							},
						},
					},
					y1: {
						position: "right",
						title: {
							align: "end",
							display: true,
							text: "%: Tỉ trọng cổ phiếu",
							padding: {
								bottom: 12,
							},
						},
						border: {
							width: 0,
						},
						grid: {
							drawOnChartArea: false,
						},
						ticks: {
							stepSize: 3,
							beginAtZero: true,
							color: "#fff",
							callback: (value) => {
								return formatNumber(value);
							},
						},
					},
				},
			},
		};
		const wrapElement = $(".combo-chart-wrapper");
		const canvas = document.createElement("canvas");
		canvas.id = COMBO_CHART_ID;
		canvas.width = wrapElement.width();
		canvas.height = wrapElement.height();
		wrapElement.append(canvas);
		const ctx = canvas.getContext("2d");
		const chart = new Chart(ctx, config);
		window.comboChart = chart;
		if (isLocal()) {
			this.randomComboChartData();
		}
	},
	updateComboChartData: ({ data = [], yTitle = [], xRotate = false }) => {
		const chart = Chart.getChart(COMBO_CHART_ID);
		if (data.length === 0) {
			console.warn("No data was provided");
			return;
		}
		const {
			data: { labels: oldLabels, datasets: oldDatasets },
		} = chart;

		const { labels, datasets } = data;
		const maxY = Math.max(...datasets[0].data);
		const magnitude = Math.pow(10, Math.floor(Math.log10(maxY)));
		const roundedMaxY = Math.ceil(maxY / magnitude) * magnitude;

		const maxY1 = Math.max(...datasets[1].data);
		function smartRoundUp(value) {
			if (value === 0) return 0;

			const magnitude = Math.pow(10, Math.floor(Math.log10(value)) - 1);

			return Math.ceil(value / magnitude) * magnitude;
		}

		const roundedMaxY1 = smartRoundUp(maxY1);

		oldLabels.length = 0;

		labels.forEach((item, index) => {
			oldLabels.push(item || `Missing label: ${index}`);
		});

		oldDatasets.forEach((item, index) => {
			item.label = datasets[index].label || "No labels";
			item.data = datasets[index].data || [];
			item.unit = datasets[index].unit || "%";
		});
		if (yTitle[0] && chart.options.scales.y.title.display) {
			chart.options.scales.y.title.text = yTitle[0];
		}
		if (yTitle[1] && chart.options.scales.y1.title.display) {
			chart.options.scales.y1.title.text = yTitle[1];
		}

		chart.options.scales.x.ticks.minRotation = xRotate ? 45 : 0;
		chart.options.scales.x.ticks.maxRotation = xRotate ? 45 : 0;
		$(".open-fund-2 .combo-chart-tab-item .loading-overlay").remove();
		if (window.innerWidth < 768) {
			chart.options.scales.y.title.display = false;
			chart.options.scales.y1.title.display = false;
		} else {
			chart.options.scales.y.title.display = true;
			chart.options.scales.y1.title.display = true;
		}

		chart.options.scales.y.max = roundedMaxY;
		if (window.innerWidth < 768) {
			chart.options.scales.y.ticks.callback = (value) => {
				let unit = "";
				let newValue = value;
				if (value >= 1000000000000) {
					unit = " " + message[lang].trillion;
					newValue = value / 1000000000000;
				} else if (value >= 1000000000) {
					unit = " " + message[lang].billion;
					newValue = value / 1000000000;
				} else if (value >= 1000000) {
					unit = " " + message[lang].million;
					newValue = value / 1000000;
				}
				return formatNumber(newValue);
			};
		}
		chart.options.scales.y1.max = roundedMaxY1;
		// console.log(maxY, maxY1);
		chart.update();
		chart.resetZoom();
		const dataPercent = datasets[1]?.data;
		if (dataPercent.length) {
			const maxYPercentage = Math.max(...dataPercent);
			const roundedMaxYPercentage = smartRoundUp(maxYPercentage) + 5;
			chart.zoomScale(
				"y1",
				{
					min: 0,
					max: roundedMaxYPercentage,
				},
				"default"
			);
		}
		if (window.innerWidth < 768) {
			chart.zoomScale(
				"x",
				{
					min: labels.length - 4,
					max: labels.length - 1,
				},
				"default"
			);
		}
	},
	randomComboChartData: () => {
		const comboChart = Chart.getChart(COMBO_CHART_ID);
		if (!comboChart) return;
		const data = {
			labels: Array.from({ length: 12 }, (_, index) => {
				const reverseIndex = 5 - index;
				const baseDate = new Date();
				const targetDate = new Date(
					baseDate.getFullYear(),
					baseDate.getMonth() - reverseIndex,
					1
				);
				return formatMonth(targetDate);
			}),
			datasets: [
				{
					label: `Giá trị đầu tư + ${new Date().getMinutes()}:${new Date().getSeconds()}`,
					data: Array.from(
						{ length: 12 },
						() => Math.random() * 100000.4
					),
					unit: "Test Unit",
				},
				{
					label: `Tỉ trọng cổ phiếu + ${new Date().getMinutes()}:${new Date().getSeconds()}`,
					data: Array.from({ length: 12 }, () => Math.random() * 10),
				},
			],
		};
		const newData = {
			data,
			yTitle: ["Left Title", "Right Title"],
			xRotate: Math.random() * 2 > 1 ? true : false,
		};

		chart.updateComboChartData(newData);
		// console.log(JSON.stringify(newData, null, 2));
	},
	createBubbleChart: () => {
		const names = [
			"DCDE",
			"VCB",
			"ACB",
			"BID",
			"VNM",
			"FPT",
			"GAS",
			"HPG",
			"MBB",
			"MSN",
			"PDR",
			"VIC",
			"VRE",
			"Vietcombank",
			"VietinBank",
			"VPBank",
			"Argos",
			"BIDV",
		];
		const data = {
			datasets: [
				{
					data: Array.from(
						{ length: Math.floor(Math.random() * 20) },
						(_, index) => ({
							x: Math.random() * 50,
							y: Math.random() * 50,
							r: Math.random() * 30,
							name: names[
								Math.floor(Math.random() * names.length)
							],
							contens: [
								{
									label: "NAV",
									content: "5.124 Tỷ đồng",
								},
								{
									label: "Top 5",
									content: "32,5%",
								},
								{
									label: "Danh mục",
									content: "87,9%",
								},
							],
						})
					),
					backgroundColor: chartColors[0],
					hoverBorderWidth: 0,
					hoverBackgroundColor: chartColors[0],
					clip: false,
					hoverRadius: false,
				},
			],
			yTitle: "Tỷ trọng  danh mục (%)",
		};
		const grid = {
			display: true,
			color: "rgba(256, 256, 256, 0.3)",
			tickColor: "transparent",
		};
		let top_value = [];
		let EXTEND_LIMIT = 5;
		function getVisibleValues(c) {
			const zoomScaleBounds = c.getZoomedScaleBounds();
			const { min: minX, max: maxX } = zoomScaleBounds.x;
			const { min: minY, max: maxY } = zoomScaleBounds.y;

			// Iterate through the original data array and update each item directly
			data.datasets[0].data.forEach((item) => {
				item.isVisible =
					item.x >= minX &&
					item.x <= maxX &&
					item.y >= minY &&
					item.y <= maxY;
			});

			chart.update();
		}
		const config = {
			type: "bubble",
			data: data,
			plugins: [
				{
					afterInit: (chart) => {
						top_value = getTopValue(chart, 5);
						const scaleFactor = window.innerWidth < 1024 ? 0.7 : 1;
						chart.data.datasets[0].data.forEach((item) => {
							item.isVisible = true;
							item.r = item.r * scaleFactor;
						});
					},
					afterUpdate: (chart) => {
						top_value = getTopValue(chart, 5);
						// console.log("🟩 ~ top_value:", top_value);
						const scaleFactor = window.innerWidth < 1024 ? 0.7 : 1;
						let minX = Infinity,
							maxX = -Infinity,
							minY = Infinity,
							maxY = -Infinity;
						chart.data.datasets[0].data.forEach((item) => {
							item.isVisible = true;
							item.r = item.isModifyRadius
								? item.r
								: item.r * scaleFactor;
							item.isModifyRadius = true;
							minX = Math.min(minX, item.x);
							maxX = Math.max(maxX, item.x);
							minY = Math.min(minY, item.y);
							maxY = Math.max(maxY, item.y);
						});
						chart.options.plugins.zoom.limits.x.min =
							minX - EXTEND_LIMIT;
						chart.options.plugins.zoom.limits.x.max =
							maxX + EXTEND_LIMIT;
						chart.options.plugins.zoom.limits.y.min =
							minY - EXTEND_LIMIT;
						chart.options.plugins.zoom.limits.y.max =
							maxY + EXTEND_LIMIT;
					},
					afterDraw: (chart) => {
						const {
							ctx,
							data,
							chartArea: { width, height },
						} = chart;
						const existingLabels = [];

						const existingLabelsElements =
							canvas.parentElement.querySelectorAll(
								".bubble-chart-label"
							);
						existingLabelsElements.forEach((label) =>
							label.remove()
						);

						const allDataPoints = chart
							.getDatasetMeta(0)
							.data.map((arc, index) => {
								const { x, y } = arc.tooltipPosition();
								const value = data.datasets[0].data[index];
								return {
									x: x,
									y: y,
									r: value.r,
									originalData: value,
								};
							});

						chart.getDatasetMeta(0).data.forEach((arc, i) => {
							const { x, y } = arc.tooltipPosition();
							const value = data.datasets[0].data[i];
							const color = data.datasets[0].backgroundColor;

							const isVisible = value.isVisible;

							const isPointInChartArea =
								x >= chart.chartArea.left &&
								x <= chart.chartArea.right &&
								y >= chart.chartArea.top &&
								y <= chart.chartArea.bottom;

							const isInTopValue = top_value.some(
								(item) =>
									item.x === value.x &&
									item.y === value.y &&
									item.r === value.r
							);

							if (isInTopValue && isPointInChartArea) {
								const tempLabel = document.createElement("div");

								tempLabel.className = "bubble-chart-label";
								tempLabel.innerText = `${value.name}`;
								tempLabel.style.position = "absolute";
								tempLabel.style.whiteSpace = "nowrap";
								tempLabel.style.visibility = "hidden";
								// tempLabel.style.display = isVisible
								// 	? "block"
								// 	: "none";
								canvas.parentElement.appendChild(tempLabel);

								const labelBounding =
									tempLabel.getBoundingClientRect();
								const labelWidth = labelBounding.width;
								const labelHeight = labelBounding.height;

								const bestPosition = findBestLabelPosition(
									x,
									y,
									value.r,
									labelWidth,
									labelHeight,
									width,
									height,
									existingLabels,
									allDataPoints
								);

								const id = `bubble-chart-label-${value.x.toFixed(
									2
								)}-${value.y.toFixed(2)}-${value.r.toFixed(2)}`;
								tempLabel.id = id;
								tempLabel.style.visibility = "visible";
								tempLabel.dataset.rawY = value.y;
								tempLabel.dataset.rawR = value.r;
								tempLabel.style.left = bestPosition.x + "px";
								tempLabel.style.top = bestPosition.y + "px";

								existingLabels.push({
									rect: {
										left: bestPosition.x,
										top: bestPosition.y,
										right: bestPosition.x + labelWidth,
										bottom: bestPosition.y + labelHeight,
									},
									element: tempLabel,
								});
							}
						});
					},
				},
			],
			options: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: {
						display: false,
					},
					title: {
						display: false,
					},
					tooltip: {
						enabled: false, // Disable default tooltip
						position: "nearest", // Position tooltip near the cursor
						external: function (context) {
							// Tooltip Element
							let tooltipEl =
								document.getElementById("bubble-tooltip");

							// Create element on first render
							if (!tooltipEl) {
								tooltipEl = document.createElement("div");
								tooltipEl.id = "bubble-tooltip";
								tooltipEl.className = "absolute bubble-tooltip";
								tooltipEl.style.pointerEvents = "none";
								tooltipEl.style.transition = "all .1s ease";
								document.body.append(tooltipEl);
							}

							const wrapBounding = wrapElement
								.get(0)
								.getBoundingClientRect();

							// Hide if no tooltip
							const tooltipModel = context.tooltip;
							const color = tooltipModel.dataPoints
								? tooltipModel.dataPoints[0].dataset
										.backgroundColor
								: chartLineStyles[0].backgroundColor;
							if (tooltipModel.opacity === 0) {
								tooltipEl.style.opacity = 0;
								tooltipEl.classList.remove("active");
								return;
							}

							// Set caret Position
							tooltipEl.classList.remove(
								"above",
								"below",
								"no-transform",
								"center",
								"top",
								"left",
								"right",
								"bottom"
							);
							const isCloseToTop =
								tooltipModel.caretY < wrapBounding.height / 2;
							if (isCloseToTop) {
								tooltipEl.classList.add("top");
							} else {
								tooltipEl.classList.add("bottom");
							}
							if (tooltipModel.xAlign) {
								tooltipEl.classList.add(tooltipModel.xAlign);
							} else {
								tooltipEl.classList.add("no-transform");
							}

							if (tooltipModel.body) {
								const titleLines = tooltipModel.title || [];

								const bodyLines = tooltipModel.body.map(
									(b) => b.lines
								);
								let innerHtml = "";

								const interactionType =
									context.chart.options.interaction.mode;
								bodyLines.forEach(function (body, i) {
									const value =
										tooltipModel.dataPoints[0].raw;
									// console.log("🟩 ~ value:", value);
									const { x, y, r, ...rest } = value;
									const name = value.name || "";

									const contens = value.contents || [];
									innerHtml += `<div class='tooltip-title tooltip-title-${i}'>${name}</div><div class="tooltip-content">
									${contens
										.map((item) => {
											return `<div class='tooltip-content-item'><span class="item-cell"><strong>${item.label}:</strong></span> <span class="item-cell">${item.content}</span></div>`;
										})
										.join("")}
									</div>`;
								});

								tooltipEl.innerHTML = innerHtml;
							}

							// Display, position, and set styles for font
							const isTooltipOnRight =
								tooltipModel.xAlign === "right";
							tooltipEl.style.opacity = 1;
							tooltipEl.classList.add("active");
							const arrowWidth = 12;
							const currentValue = tooltipModel.dataPoints[0].raw;
							// console.log("🟩 ~ currentValue:", currentValue);
							const position =
								context.chart.canvas.getBoundingClientRect();
							const tooltipLeft = isTooltipOnRight
								? position.left +
								  window.scrollX +
								  tooltipModel.caretX -
								  tooltipEl.offsetWidth -
								  currentValue.r
								: position.left +
								  window.scrollX +
								  tooltipModel.caretX -
								  arrowWidth +
								  currentValue.r * 2;
							tooltipEl.style.left = tooltipLeft + "px";
							tooltipEl.style.top = isCloseToTop
								? position.top +
								  window.scrollY +
								  tooltipModel.caretY +
								  "px"
								: position.top +
								  window.scrollY +
								  tooltipModel.caretY -
								  tooltipEl.offsetHeight -
								  arrowWidth +
								  "px";
							tooltipEl.style.setProperty("--color", color);
						},
					},
					zoom: {
						pan: {
							enabled: true,
							onPan: ({ chart, trigger }) => {
								getVisibleValues(chart);
							},
						},
						limits: {
							y: {
								min: "original",
								max: "original",
								minRange: 20,
							},
							x: {
								min: "original",
								max: "original",
								minRange: 20,
							},
						},
						zoom: {
							mode: "xy",
							wheel: {
								enabled: true,
							},
							pinch: {
								enabled: true,
							},
							onZoom: ({ chart, trigger }) => {
								getVisibleValues(chart);
							},
						},
					},
				},
				title: {
					font: {
						size: fontSize(14),
						weight: 400,
						family: "Inter Display",
						color: "#fff",
					},
				},
				font: {
					size: fontSize(16),
					weight: 400,
					family: "Inter Display",
					color: "#fff",
				},
				scales: {
					x: {
						border: {
							width: 3,
							color: "#fff",
						},
						grid,
						ticks: {
							padding: 20,
							beginAtZero: true,
							color: "#fff",
							stepSize: 5,
							callback: (value) => {
								return formatNumber(value, "%");
							},
						},
					},
					y: {
						title: {
							display: true,
							text: "Tỷ trọng  danh mục (%)",
							align: "end",
						},
						border: {
							display: false,
						},
						grid,
						ticks: {
							stepSize: 5,
							// beginAtZero: true,
							color: "#fff",
							callback: (value) => {
								return formatNumber(value, "%");
							},
						},
					},
				},
			},
		};
		const wrapElement = $(".bubble-chart-wrapper");
		const canvas = document.createElement("canvas");
		canvas.id = BUBBLE_CHART_ID;
		canvas.width = wrapElement.width();
		canvas.height = wrapElement.height();
		wrapElement.prepend(canvas);
		const ctx = canvas.getContext("2d");
		const chart = new Chart(ctx, config);
		window.bubbleChart = chart;
	},
	updateBubbleChartData: (data = [], yTitle = "") => {
		const chart = Chart.getChart(BUBBLE_CHART_ID);
		if (!chart) return;
		// console.log(chart.data.datasets[0].data);
		if (yTitle) {
			chart.options.scales.y.title.text = yTitle;
		}
		if (data.length === 0) {
			console.warn("No data was provided");
			return;
		}
		chart.data.datasets[0].data = data;
		chart.update();
		chart.resetZoom();

		$(".bubble-chart-tab-item .loading-overlay").remove();
	},
	randomBubbleChartData: () => {
		const chart = Chart.getChart(BUBBLE_CHART_ID);
		if (!chart) return;
		const data = [];
		const newData = Array.from({ length: 20 }, (_, index) => {
			return {
				x: Math.random() * 50,
				y: Math.random() * 50,
				r: Math.random() * 30,
				name: "VNM " + index,
				contents: [
					{
						label: "NAV " + index,
						content: "5.124 Tỷ đồng",
					},
					{
						label: "Top 5",
						content: "32,5%",
					},
				],
			};
		});
		chart.data.datasets[0].data = newData;
		$(".bubble-chart-tab-item").find(".loading-overlay").remove();
		chart.update();
	},
	init: () => {
		chart.createSelect2();
		chart.createDatePicker();
		if ($(".chart-page").length > 0) {
			chart.implementChartJS();
		}
		// chart.createPieChart();
	},
};

export default chart;

export const chartLineStyles = [
	{
		borderColor: chartColors[0],
		backgroundColor: chartColors[0],
		pointBackgroundColor: chartColors[0],
		pointBorderColor: chartColors[0],
		borderWidth: 2,
		tension: 0.5,
		pointRadius: 0,
		pointHoverRadius: 6,
		pointBorderWidth: 2,
	},
	{
		borderColor: chartColors[1],
		backgroundColor: chartColors[1],
		pointBackgroundColor: chartColors[1],
		pointBorderColor: chartColors[1],
		borderWidth: 2,
		tension: 0.5,
		pointRadius: 0,
		pointHoverRadius: 6,
		pointBorderWidth: 2,
	},
];

function getWidthRatio(number) {
	const screenWidth = window.innerWidth;
	if (screenWidth >= 1920) {
		return number;
	} else if (screenWidth >= 1200) {
		return (screenWidth / 1920) * number * 0.8;
	} else {
		return (1200 / 1920) * number;
	}
}

export const chartComboStyle = [
	{
		borderColor: chartColors[0],
		backgroundColor: chartColors[0],
		order: 1,
		barPercentage: 0.75,
	},
	{
		borderWidth: 1,
		borderColor: chartColors[1],
		backgroundColor: chartColors[1],
		type: "line",
		order: 0,
		yAxisID: "y1",
		pointBorderWidth: getWidthRatio(16),
		pointHoverRadius: 4,
		datalabels: {
			labels: {
				index: {
					align: function (context) {
						const yValue = context.dataset.data[context.dataIndex];
						const y1ScaleMax = context.chart.scales.y1.max;
						if (yValue >= y1ScaleMax * 0.8) {
							return "bottom";
						}
						return "top";
					},
					anchor: "center",
					formatter: function (value, ctx) {
						// ctx.active
						return formatNumber(value, "%");
					},
					clip: true,
					offset: function (context) {
						const yValue =
							context.dataset.data[context.dataIndex].y;
						const y1ScaleMax = context.chart.scales.y1.max;
						if (yValue >= y1ScaleMax * 0.9) {
							return 12;
						}
						return 8;
					},
				},
			},
		},
	},
];

const tablesToolTips = [];
let isSetSortTable = false;
export const tableToolTip = {
	createTooltip: () => {
		Object.keys(tablesToolTips).forEach((key) => {
			delete tablesToolTips[key];
		});
		const tables = $(".table-responsive");
		tables.each(function (e) {
			const $currentTable = $(this);
			const dataTitles = $currentTable.find("th[data-title]");
			if (!dataTitles.length) return;
			$currentTable.attr("id", `table-tooltip-${e}`);
			$currentTable.addClass("table-tooltip-wrapper");
			dataTitles.each(function (titleIndex) {
				const randomID = `table-tooltip-${e}-${titleIndex}`;
				$(`.table-tooltip[id="${randomID}"]`).remove();
				const offset = $(this).offset();
				$(this).attr("id", randomID);
				const title = $(this).text();
				const content = $(this).attr("data-title");
				const tooltip = `<div class="table-tooltip" id="${randomID}">
			<div class="table-tooltip-title">${title}</div>
			<div class="table-tooltip-content">${content}</div>
			</div>`;
				$currentTable
					.closest(".table-responsive-wrapper")
					.append(tooltip);
				const dom = $(`.table-tooltip[id="${randomID}"]`);
				if (!dom.length) return;
				const tooltipBound = dom.get(0).getBoundingClientRect();
				const marginOffset = offset.left - $currentTable.offset().left;
				const isInTableLimit =
					dom.width() + marginOffset < $currentTable.width();

				const isLastChild = titleIndex === dataTitles.length - 1;
				if (!isInTableLimit) {
					dom.addClass("position-right");
				}
				const index = dom.index();
				const reverseIndex = dataTitles.length - index - 1;
				const beforeElementIndex = titleIndex;
				const beforeElement = $(dataTitles[beforeElementIndex]);
				dom.css({
					top: -tooltipBound.height,
					[isInTableLimit ? "left" : "right"]: isInTableLimit
						? marginOffset
						: isLastChild
						? 0
						: beforeElement.outerWidth(),
				});
			});
			const toolTips = dataTitles
				.map(function () {
					return {
						id: $(this).attr("id"),
						title: $(this).text(),
						content: $(this).attr("data-title"),
					};
				})
				.get();
			tablesToolTips[$currentTable.attr("id")] = toolTips;
		});
		if (!isSetSortTable) {
			isSetSortTable = true;
			tableToolTip.sortTable();
		}
		// const $tbodyWrapper = tables.find(".wrapper-tbody");
		// console.log("🟩 ~ $tbodyWrapper:", $tbodyWrapper);
		// if ($tbodyWrapper) {
		// 	$tbodyWrapper.css({ width: "auto" });
		// 	setTimeout(() => {
		// 		$tbodyWrapper.css({ width: tables.outerWidth() });
		// 	}, 100);
		// }
	},
	handleTooltip: () => {
		$(document).off("mouseenter click", "[data-title]", handleToolTip);
		$(document).on("mouseenter click", "[data-title]", handleToolTip);

		function handleToolTip(e) {
			const $currentTable = $(e.target).closest(".table-tooltip-wrapper");
			const currentTooltipID = $(e.target)
				.closest("[data-title]")
				.attr("id");
			const toolTip = $(`.table-tooltip[id="${currentTooltipID}"]`);
			let left = $(e.target).offset().left - $currentTable.offset().left;
			const isReachMaxWidth =
				left + toolTip.outerWidth() > $currentTable.width();
			if (
				left + toolTip.outerWidth() > $currentTable.width() &&
				!isReachMaxWidth
			) {
				left =
					$currentTable.width() -
					toolTip.width() -
					$(e.target).outerWidth();
			}
			if (
				left + toolTip.outerWidth() > $currentTable.width() &&
				isReachMaxWidth
			) {
				left = $currentTable.width() - toolTip.width();
			}
			if (window.innerWidth < 768) {
				left = 0;
			}
			toolTip.css({
				left: left,
			});
			toolTip.addClass("active");
			$(e.target).on("mouseleave", function () {
				toolTip.removeClass("active");
				$(e.target).off("mouseleave", handleToolTip);
			});
		}
	},
	sortTable: () => {
		let lastTranslate = 0;
		if ($(".table-tooltip-wrapper table").length) {
			let isChangeMonth = false;
			const $table = $(".table-tooltip-wrapper table");
			appendScripts([`${scriptPath}/isotope.pkgd.min.js`]).then(() => {
				let lastWidths = [];
				function setWidthTable() {
					if (iOS()) {
						const firstTrHeight = $table
							.find("tbody tr:visible")
							.first()
							.outerHeight();
						if (lastTranslate === firstTrHeight) return;
						$table.css({ paddingTop: firstTrHeight });
						$table.find("thead").css({
							transform: `translateY(-${firstTrHeight}px)`,
						});
						lastTranslate = firstTrHeight;
					}
					const widths = $table
						.find("th")
						.map(function () {
							return $(this).outerWidth();
						})
						.get();
					if (JSON.stringify(lastWidths) !== JSON.stringify(widths)) {
						// Update width of table
						$table.find("tbody tr").each(function () {
							$(this)
								.find("td")
								.each(function (index) {
									$(this).outerWidth(widths[index]);
								});
						});
					}
					lastWidths = widths;
					requestAnimationFrame(setWidthTable);
				}

				let currentCategory = $("#rating-system-select").val();
				let currentMonth = $("#rating-system-month-select").val();

				var filters = {};

				// $(
				// 	".table-tooltip-wrapper tbody tr:not([data-month='03-2025'])"
				// ).remove();
				const $grid = $(".table-tooltip-wrapper table").isotope({
					itemSelector: ".table-tooltip-wrapper table tbody tr",
					layoutMode: "vertical",
					getSortData: {
						id: ".id parseInt",
						name: ".name",
						// "per-years": (item) => sortNumber(item, ".per-years"),
						"per-years": function (itemElem) {
							var value =
								$(itemElem).data("value") ||
								$(itemElem).find(".per-years").text();
							return parseFloat(value.replace(/[\(\)]/g, ""));
						},
						nav: function (itemElem) {
							var value =
								$(itemElem).find(".nav").data("value") ||
								$(itemElem).find(".nav").text();
							return parseFloat(
								value.toString().replace(/[\(\)]/g, "")
							);
						},
						"per-5y": function (itemElem) {
							var value =
								$(itemElem).find(".per-5y").data("value") ||
								$(itemElem).find(".per-5y").text();
							return parseFloat(
								value.toString().replace(/[\(\)]/g, "")
							);
						},
						"per-1y": function (itemElem) {
							var value =
								$(itemElem).find(".per-1y").data("value") ||
								$(itemElem).find(".per-1y").text();
							return parseFloat(
								value.toString().replace(/[\(\)]/g, "")
							);
						},
						mdd: function (itemElem) {
							var value =
								$(itemElem).find(".mdd").data("value") ||
								$(itemElem).find(".mdd").text();
							return parseFloat(
								value.toString().replace(/[\(\)]/g, "")
							);
						},
						dd: function (itemElem) {
							var value =
								$(itemElem).find(".dd").data("value") ||
								$(itemElem).find(".dd").text();
							return parseFloat(
								value.toString().replace(/[\(\)]/g, "")
							);
						},
						total: function (itemElem) {
							var value =
								$(itemElem).find(".total").data("value") ||
								$(itemElem).find(".total").text();
							return parseFloat(
								value.toString().replace(/[\(\)]/g, "")
							);
						},
						selectedCategory: function (itemElem) {
							return $(itemElem).data("name") === currentCategory
								? 0
								: 1;
						},
					},
					filter: function () {
						var isCategoryMatch = true;
						var isMonthMatch = true;

						// Check the category filter
						if (filters.category && filters.category !== "*") {
							isCategoryMatch =
								$(this).data("name") === filters.category;
						}

						// Check the month filter
						if (filters.month && filters.month !== "*") {
							isMonthMatch =
								$(this).data("month") === filters.month;
						}

						// Return true only if both conditions are met
						return isCategoryMatch && isMonthMatch;
					},
				});
				window.$grid = $grid;
				let timeout = null;
				// $grid.on("arrangeComplete", function (event, filteredItems) {
				// 	clearTimeout(timeout);
				// 	timeout = setTimeout(() => {
				// 		setWidthTable();
				// 	}, 100);
				// });
				let currentSort = { key: null, ascending: true };

				$(".table-responsive thead tr").each(function () {
					const isExitModify = $(this).find(
						".tablesorter-header-inner"
					).length;
					if (isExitModify) return;
					$(this)
						.find("th")
						.each(function () {
							$(this).wrapInner(
								`<div class="tablesorter-header-inner"></div>`
							);
						});
				});

				$("#rating-system-select").on("change", function () {
					if ($(this).val() === currentCategory) return;
					currentCategory = $(this).val();
					// $grid.isotope("updateSortData").isotope({
					// 	sortBy: "selectedCategory",
					// });
					$("[data-sort-by]").removeClass("sort-asc sort-desc");
					const $table = $(".table-tooltip-wrapper");
					const otherTr = $table.find(
						`tbody tr:not([data-name="${currentCategory}"]):visible`
					);
					const activeTr = $table.find(
						`tbody tr[data-name="${currentCategory}"]:visible`
					);
					if (currentCategory === "*") {
						$table.animate(
							{
								scrollTop: 0,
							},
							"fast",
							function () {
								otherTr.removeClass("active");
							}
						);
					} else {
						$table.animate(
							{
								scrollTop: activeTr.position().top,
							},
							"fast",
							function () {
								otherTr.removeClass("active");
								activeTr.addClass("active");
							}
						);
					}
				});

				$("#rating-system-month-select").on("change", function () {
					if ($(this).val() === currentMonth) return;
					currentMonth = $(this).val();
					const $table = $(".table-tooltip-wrapper table");
					$table
						.find(`tbody tr:not([data-month="${currentMonth}"])`)
						.each(function (index) {
							$(this).hide();
							$(this).addClass("month-unmatch");
							$(this).removeClass("month-match");
						});
					resetTableOther();
					$(".table-responsive").scrollTop(0.5);
					$grid.isotope("updateSortData").isotope({
						sortBy: "point",
						sortAscending: false,
					});
					$("[data-sort-by]").removeClass("sort-asc sort-desc");
				});

				function resetTableOther() {
					const $table = $(".table-tooltip-wrapper table");
					$table
						.find(`tbody tr[data-month="${currentMonth}"]`)
						.each(function (index) {
							$(this).show();
							$(this).removeClass("month-unmatch");
							$(this).addClass("month-match");
							$(this)
								.find("td:first")
								.text(index + 1);
						});
				}

				setWidthTable();

				if (!isChangeMonth) {
					isChangeMonth = true;
					$("#rating-system-month-select").trigger("change");
					$grid.isotope("layout", {
						sortBy: "point",
						sortAscending: false,
					});
					$(".table-tooltip-wrapper").css("opacity", 1);
					resetTableOther();
					// setWidthTable();
				}

				$(document).on("click", "[data-sort-by]", function () {
					const sortBy = $(this).attr("data-sort-by");

					if (currentSort.key === sortBy) {
						currentSort.ascending = !currentSort.ascending;
					} else {
						currentSort.key = sortBy;
						currentSort.ascending = false;
					}

					$("[data-sort-by]").removeClass("sort-asc sort-desc");
					$(this).addClass(
						currentSort.ascending ? "sort-asc" : "sort-desc"
					);

					// Re-apply filters and sort
					$grid.isotope({
						sortBy: sortBy,
						sortAscending: currentSort.ascending,
					});
				});

				const tableSortEvent = new CustomEvent(
					"table-sort-initialized"
				);
				document.body.dispatchEvent(tableSortEvent);
				//
			});
		}
	},
	init: () => {
		tableToolTip.createTooltip();
		tableToolTip.handleTooltip();
	},
};

const isLocal = () => {
	return window.location.hostname === "localhost";
};

function iOS() {
	return (
		[
			"iPad Simulator",
			"iPhone Simulator",
			"iPod Simulator",
			"iPad",
			"iPhone",
			"iPod",
		].includes(navigator.platform) ||
		// iPad on iOS 13 detection
		(navigator.userAgent.includes("Mac") && "ontouchend" in document)
	);
}
